// Per-business AI voice-agent provider integrations.
//
// Points cpass at the runtime (voicex) a business's agents live on. Same
// verification-first shape as Call Data Sources: the only way to know the base
// URL and credentials are right is to Test Connection against the live service.
import React, { useEffect, useState } from 'react';
import { Bot, Plus, Pencil, Trash2, PlugZap, Check, X, AlertTriangle, Loader2, Building2 } from 'lucide-react';
import { aiProvidersService, AiProvider, ProviderTestResult, ProviderBusiness } from 'api/services/aiProviders.service';

const BLANK: AiProvider = {
  name: '', providerType: 'VOICEX', baseUrl: '', authType: 'NONE',
  authHeader: '', authUsername: '', authSecret: '', tokenUrl: '', enabled: true, connectTimeoutSeconds: 10,
};

const isObjectId = (v?: string) => !!v && /^[0-9a-fA-F]{24}$/.test(v);

const VoiceAiIntegrations: React.FC = () => {
  const [rows, setRows] = useState<AiProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AiProvider | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ProviderTestResult>>({});

  // Business binding — a separate flow from the edit form because both of its
  // options talk to the live provider (list its businesses, or create one there).
  const [linking, setLinking] = useState<AiProvider | null>(null);
  const [businesses, setBusinesses] = useState<ProviderBusiness[]>([]);
  const [bizLoading, setBizLoading] = useState(false);
  const [bizError, setBizError] = useState<string | null>(null);
  const [bizChoice, setBizChoice] = useState<string>('');
  const [newBizName, setNewBizName] = useState('');
  const [bizSaving, setBizSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const r = await aiProvidersService.list();
    if (r.success) setRows(r.data || []); else setError(r.error || 'Failed to load');
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!editing) return;
    setSaving(true); setError(null);
    const r = editing.id
      ? await aiProvidersService.update(editing.id, editing)
      : await aiProvidersService.create(editing);
    setSaving(false);
    if (r.success) { setEditing(null); load(); } else setError(r.error || 'Save failed');
  };

  const remove = async (row: AiProvider) => {
    if (!row.id) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete "${row.name}"? The stored credential is removed with it.`)) return;
    const r = await aiProvidersService.remove(row.id);
    if (r.success) load(); else setError(r.error || 'Delete failed');
  };

  const test = async (row: AiProvider) => {
    if (!row.id) return;
    setTesting(row.id);
    const r = await aiProvidersService.test(row.id);
    setTesting(null);
    if (r.success && r.data) { setResults(p => ({ ...p, [row.id!]: r.data! })); load(); }
    else setResults(p => ({ ...p, [row.id!]: { ok: false, message: r.error || 'Test failed' } }));
  };

  const openLink = async (row: AiProvider) => {
    if (!row.id) return;
    setLinking(row);
    setBizError(null); setNewBizName(''); setBusinesses([]);
    setBizChoice(row.voicexBusinessId || '');
    setBizLoading(true);
    const r = await aiProvidersService.listBusinesses(row.id);
    setBizLoading(false);
    if (r.success) setBusinesses(r.data || []);
    // Not fatal — the provider may be unreachable, but creating one may still work.
    else setBizError(r.error || 'Could not load businesses');
  };

  /** Bind to an existing business by saving the id onto the provider. */
  const bindExisting = async () => {
    if (!linking?.id || !bizChoice) return;
    setBizSaving(true); setBizError(null);
    // authSecret blank = keep the stored one; the API never returns it.
    const r = await aiProvidersService.update(linking.id, { ...linking, authSecret: '', voicexBusinessId: bizChoice });
    setBizSaving(false);
    if (r.success) { setLinking(null); load(); } else setBizError(r.error || 'Failed to link the business');
  };

  /** Create a business on the provider; it mints the id and we bind to it. */
  const createAndBind = async () => {
    if (!linking?.id || !newBizName.trim()) return;
    setBizSaving(true); setBizError(null);
    const r = await aiProvidersService.createBusiness(linking.id, newBizName.trim());
    setBizSaving(false);
    if (r.success) { setLinking(null); load(); } else setBizError(r.error || 'Failed to create the business');
  };

  const input = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent';
  const lbl = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <Bot className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            Voice AI integrations
          </h1>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            The AI voice-agent runtime this organization's agents are created and run on.
            Different businesses can point at different servers or accounts.
          </p>
        </div>
        <button className="tl-btn tl-btn-primary" onClick={() => setEditing({ ...BLANK })}>
          <Plus className="w-4 h-4" /> Add provider
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2">{error}</div>}

      {loading ? (
        <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="tl-card text-center py-10">
          <Bot className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm font-medium text-gray-900 dark:text-white">No provider configured</p>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            Add the voice-agent runtime this organization uses.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(row => {
            const res = row.id ? results[row.id] : undefined;
            const ok = res ? res.ok : row.lastTestOk;
            const msg = res ? res.message : row.lastTestMessage;
            return (
              <div key={row.id} className="tl-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 dark:text-white">{row.name}</span>
                      <span className="tl-pill tl-pill-coral">{row.providerType}</span>
                      {row.authType !== 'NONE' && <span className="tl-pill">{row.authType}</span>}
                      {!row.enabled && <span className="tl-pill">Disabled</span>}
                      {ok === true && <span className="tl-pill tl-pill-success"><Check className="w-3 h-3" /> Reachable</span>}
                      {ok === false && <span className="tl-pill tl-pill-danger"><X className="w-3 h-3" /> Unreachable</span>}
                      {row.voicexBusinessId
                        ? <span className="tl-pill"><Building2 className="w-3 h-3" /> Business linked</span>
                        : <span className="tl-pill tl-pill-amber"><AlertTriangle className="w-3 h-3" /> No business linked</span>}
                    </div>
                    <div className="text-[12.5px] mt-1 font-mono break-all" style={{ color: 'var(--text-muted)' }}>
                      {row.baseUrl}
                    </div>
                    {/* Without a linked business, agents push unattributed and can never be listed back. */}
                    {row.voicexBusinessId ? (
                      <div className="text-[12px] mt-1 font-mono" style={{ color: 'var(--text-muted)' }}>
                        business {row.voicexBusinessId}
                      </div>
                    ) : (
                      <div className="text-[12px] mt-1.5 px-2.5 py-1.5 rounded-lg flex items-start gap-1.5"
                           style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>Agents created here won't be attributed to a business on the runtime, so they can't be listed back. Link one.</span>
                      </div>
                    )}
                    {msg && (
                      <div className="text-[12px] mt-1.5" style={{ color: ok ? 'var(--success-text)' : 'var(--danger-text)' }}>
                        {msg}
                      </div>
                    )}
                    {res && !res.ok && res.hint && (
                      <div className="text-[12px] mt-1.5 px-2.5 py-1.5 rounded-lg flex items-start gap-1.5"
                           style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>{res.hint}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => test(row)} disabled={testing === row.id}>
                      {testing === row.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />}
                      Test
                    </button>
                    <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => openLink(row)} title="Link the business agents belong to">
                      <Building2 className="w-3.5 h-3.5" /> Business
                    </button>
                    <button className="tl-icon-btn" title="Edit" onClick={() => setEditing({ ...row, authSecret: '' })}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="tl-icon-btn" title="Delete" onClick={() => remove(row)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'var(--scrim)' }}>
          <div className="tl-card w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              {editing.id ? 'Edit provider' : 'Add provider'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Name</label>
                <input className={input} value={editing.name}
                       onChange={e => setEditing({ ...editing, name: e.target.value })}
                       placeholder="CatchMyCall Voicex" />
              </div>
              <div>
                <label className={lbl}>Provider</label>
                <select className={input} value={editing.providerType}
                        onChange={e => setEditing({ ...editing, providerType: e.target.value as any })}>
                  <option value="VOICEX">Voicex</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Base URL</label>
                <input className={input + ' font-mono'} value={editing.baseUrl}
                       onChange={e => setEditing({ ...editing, baseUrl: e.target.value })}
                       placeholder="http://15.206.244.70:8082/voicex-api-service" />
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Include the context path. Agent calls are made relative to this.
                </p>
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Agent websocket URL</label>
                <input className={input + ' font-mono'} value={editing.wsUrlTemplate || ''}
                       onChange={e => setEditing({ ...editing, wsUrlTemplate: e.target.value })}
                       placeholder="wss://core-voicex.catchmycall.ai/chat/v2/{agent_id}" />
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Where each agent's media socket lives. Use <span className="font-mono">{'{agent_id}'}</span>, or
                  leave it off the end and the agent id is appended. This cannot be worked out from the base
                  URL — the socket is usually a different host and path — so an agent saved without it simply
                  has no socket URL.
                </p>
                {editing.wsUrlTemplate && !/^wss?:\/\//i.test(editing.wsUrlTemplate.trim()) && (
                  <p className="text-[11px] mt-1" style={{ color: 'var(--danger, #dc2626)' }}>
                    Must start with ws:// or wss:// — this looks like the REST base URL.
                  </p>
                )}
              </div>
              <div>
                <label className={lbl}>Auth type</label>
                <select className={input} value={editing.authType}
                        onChange={e => setEditing({ ...editing, authType: e.target.value as any })}>
                  <option value="OAUTH2_CLIENT_CREDENTIALS">OAuth2 client credentials (Voicex)</option>
                  <option value="NONE">None</option>
                  <option value="API_KEY">API key (header)</option>
                  <option value="BEARER">Bearer token</option>
                  <option value="BASIC">Basic (user + secret)</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Connect timeout (seconds)</label>
                <input type="number" min={1} max={60} className={input} value={editing.connectTimeoutSeconds}
                       onChange={e => setEditing({ ...editing, connectTimeoutSeconds: parseInt(e.target.value || '10') })} />
              </div>
              {editing.authType === 'API_KEY' && (
                <div className="sm:col-span-2">
                  <label className={lbl}>Header name</label>
                  <input className={input} value={editing.authHeader || ''}
                         onChange={e => setEditing({ ...editing, authHeader: e.target.value })}
                         placeholder="X-API-Key" />
                </div>
              )}
              {editing.authType === 'BASIC' && (
                <div className="sm:col-span-2">
                  <label className={lbl}>Username</label>
                  <input className={input} value={editing.authUsername || ''}
                         onChange={e => setEditing({ ...editing, authUsername: e.target.value })} />
                </div>
              )}
              {editing.authType === 'OAUTH2_CLIENT_CREDENTIALS' && (
                <>
                  <div className="sm:col-span-2">
                    <label className={lbl}>Client ID</label>
                    <input className={input + ' font-mono'} value={editing.authUsername || ''}
                           onChange={e => setEditing({ ...editing, authUsername: e.target.value })}
                           placeholder="client id" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={lbl}>
                      Token URL <span style={{ color: 'var(--text-muted)' }}>— optional</span>
                    </label>
                    <input className={input + ' font-mono'} value={editing.tokenUrl || ''}
                           onChange={e => setEditing({ ...editing, tokenUrl: e.target.value })}
                           placeholder={editing.baseUrl ? `${editing.baseUrl.replace(/\/$/, '')}/v1/token` : 'leave blank to derive from the base URL'} />
                    <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                      Leave blank to use <span className="font-mono">{'{base URL}'}/v1/token</span> — where Voicex remaps
                      the standard <span className="font-mono">/oauth/token</span> to.
                      {editing.effectiveTokenUrl && (
                        <> Currently: <span className="font-mono break-all">{editing.effectiveTokenUrl}</span></>
                      )}
                    </p>
                  </div>
                </>
              )}
              {editing.authType !== 'NONE' && (
                <div className="sm:col-span-2">
                  <label className={lbl}>
                    {editing.authType === 'OAUTH2_CLIENT_CREDENTIALS' ? 'Client secret' : 'Secret'}
                    {editing.id && <span style={{ color: 'var(--text-muted)' }}> — leave blank to keep the current one</span>}
                  </label>
                  <input type="password" className={input} value={editing.authSecret || ''}
                         onChange={e => setEditing({ ...editing, authSecret: e.target.value })}
                         autoComplete="new-password"
                         placeholder={editing.authType === 'BEARER' ? 'token'
                           : editing.authType === 'BASIC' ? 'password'
                           : editing.authType === 'OAUTH2_CLIENT_CREDENTIALS' ? 'client secret' : 'api key'} />
                  <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    Encrypted at rest and never returned by the API.
                  </p>
                </div>
              )}
              <div className="sm:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={!!editing.enabled}
                         onChange={e => setEditing({ ...editing, enabled: e.target.checked })} />
                  Enabled
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="tl-btn tl-btn-neutral" onClick={() => setEditing(null)}>Cancel</button>
              <button className="tl-btn tl-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {linking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'var(--scrim)' }}>
          <div className="tl-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Building2 className="w-4 h-4" style={{ color: 'var(--accent)' }} />
              Business on {linking.name}
            </h2>
            <p className="text-[12.5px] mt-1 mb-4" style={{ color: 'var(--text-muted)' }}>
              Agents created here are attributed to this business on the runtime. It's the runtime's own
              identifier, not a cpass one — pick an existing business or create one.
            </p>

            {bizError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2 mb-3">{bizError}</div>
            )}

            {linking.voicexBusinessId && (
              <div className="text-[12px] mb-3 px-2.5 py-1.5 rounded-lg flex items-start gap-1.5"
                   style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Already linked to <span className="font-mono">{linking.voicexBusinessId}</span>. Changing it
                  leaves agents already created under the old business behind — they won't follow.
                </span>
              </div>
            )}

            <label className={lbl}>Existing business</label>
            {bizLoading ? (
              <div className="flex items-center gap-2 text-sm py-2" style={{ color: 'var(--text-muted)' }}>
                <Loader2 className="w-4 h-4 animate-spin" /> Loading from the provider…
              </div>
            ) : businesses.length === 0 ? (
              <p className="text-[13px] py-1" style={{ color: 'var(--text-muted)' }}>
                No businesses returned by the provider.
              </p>
            ) : (
              <select className={input} value={bizChoice} onChange={e => setBizChoice(e.target.value)}>
                <option value="">— select —</option>
                {businesses.map(b => (
                  <option key={b.businessId} value={b.businessId}>
                    {b.name || '(unnamed)'} — {b.businessId}{b.selected ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            )}
            <div className="flex justify-end mt-2">
              <button className="tl-btn tl-btn-primary tl-btn-sm"
                      onClick={bindExisting}
                      disabled={bizSaving || !isObjectId(bizChoice) || bizChoice === linking.voicexBusinessId}>
                {bizSaving ? 'Linking…' : 'Link selected'}
              </button>
            </div>

            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1" style={{ background: 'var(--border)' }} />
              <span className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>or</span>
              <div className="h-px flex-1" style={{ background: 'var(--border)' }} />
            </div>

            <label className={lbl}>Create a new business on the provider</label>
            <input className={input} value={newBizName} onChange={e => setNewBizName(e.target.value)}
                   placeholder="CatchMyCall" />
            <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
              The provider assigns the ID and this provider is linked to it automatically.
            </p>
            <div className="flex justify-end mt-2">
              <button className="tl-btn tl-btn-secondary tl-btn-sm"
                      onClick={createAndBind}
                      disabled={bizSaving || !newBizName.trim() || !!linking.voicexBusinessId}
                      title={linking.voicexBusinessId ? 'Already linked — unlink first' : undefined}>
                {bizSaving ? 'Creating…' : 'Create and link'}
              </button>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button className="tl-btn tl-btn-neutral" onClick={() => setLinking(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceAiIntegrations;
